import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Button,
    Typography,
    Box,
    TablePagination,
    Chip,
    Snackbar,
    Alert,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CustomerModal from './CustomerModal';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import { formatDate } from '../../../utils/dateUtils';
import api from '../../../services/api';

interface User {
    _id: string;
    name: string;
    email: string;
    role: 'admin' | 'client';
    active: boolean;
    createdAt: string;
}

interface SnackbarMessage {
    message: string;
    severity: 'success' | 'error';
}

type UserRole = 'all' | 'admin' | 'client';

const CustomerList: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [snackbar, setSnackbar] = useState<SnackbarMessage | null>(null);
    const [roleFilter, setRoleFilter] = useState<UserRole>('all');

    const showMessage = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(null);
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const params: any = {
                page: page + 1,
                limit: rowsPerPage
            };
            
            if (roleFilter !== 'all') {
                params.role = roleFilter;
            }

            const response = await api.get('/admin/users', { params });
            setUsers(response.data.data.users);
            setTotalUsers(response.data.pagination.total);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching users:', error);
            showMessage('Error fetching users', 'error');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [page, rowsPerPage, roleFilter]);

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRoleFilterChange = (event: React.MouseEvent<HTMLElement>, newRole: UserRole) => {
        if (newRole !== null) {
            setRoleFilter(newRole);
            setPage(0);
        }
    };

    const handleOpenModal = (user?: User) => {
        setSelectedUser(user || null);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setSelectedUser(null);
        setModalOpen(false);
    };

    const handleOpenDeleteDialog = (user: User) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setUserToDelete(null);
        setDeleteDialogOpen(false);
    };

    const handleSaveUser = async (userData: Partial<User>) => {
        try {
            if (selectedUser) {
                await api.put(`/admin/users/${selectedUser._id}`, userData);
                showMessage('User updated successfully', 'success');
            } else {
                await api.post('/admin/users', userData);
                showMessage('User created successfully', 'success');
            }
            handleCloseModal();
            fetchUsers();
        } catch (error) {
            console.error('Error saving user:', error);
            showMessage('Error saving user', 'error');
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            await api.delete(`/admin/users/${userToDelete._id}`);
            showMessage('User deleted successfully', 'success');
            handleCloseDeleteDialog();
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            showMessage('Error deleting user', 'error');
        }
    };

    const handleToggleUserStatus = async (user: User) => {
        try {
            await api.patch(`/admin/users/${user._id}/status`, {
                active: !user.active
            });
            showMessage('User status updated successfully', 'success');
            fetchUsers();
        } catch (error) {
            console.error('Error updating user status:', error);
            showMessage('Error updating user status', 'error');
        }
    };

    if (loading) {
        return <Typography>Loading...</Typography>;
    }

    return (
        <Box sx={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3 
            }}>
                <Typography variant="h5">Customer Management</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal()}
                >
                    Add Customer
                </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                    value={roleFilter}
                    exclusive
                    onChange={handleRoleFilterChange}
                    size="small"
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="client">Clients</ToggleButton>
                    <ToggleButton value="admin">Admins</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <TableContainer 
                component={Paper}
                sx={{ 
                    width: '100%',
                    '& .MuiTable-root': {
                        width: '100%'
                    }
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user._id}>
                                <TableCell>{user.name}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>
                                    <Chip 
                                        label={user.role} 
                                        color={user.role === 'admin' ? 'primary' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={user.active ? 'Active' : 'Inactive'}
                                        color={user.active ? 'success' : 'error'}
                                        onClick={() => handleToggleUserStatus(user)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{formatDate(user.createdAt)}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleOpenModal(user)} size="small">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleOpenDeleteDialog(user)} size="small">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={totalUsers}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <CustomerModal
                open={modalOpen}
                onClose={(refresh: boolean) => {
                    handleCloseModal();
                    if (refresh) fetchUsers();
                }}
                user={selectedUser}
                onMessage={showMessage}
            />

            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                onConfirm={handleDeleteUser}
                title="Delete User"
                message="Are you sure you want to delete this user? This action cannot be undone."
            />

            <Snackbar
                open={!!snackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar?.severity || 'info'} 
                    sx={{ width: '100%' }}
                >
                    {snackbar?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CustomerList;
